/* eslint-disable no-magic-numbers -- 3D navigation math and rendering tolerances */
/* eslint-disable max-lines, max-depth -- moved verbatim from web3d; splitting it is tracked by MERCATOR-322 */
import type { Api } from '@technology/web3d/dist/esnext/Api.js';
import type {
  PointerInput,
  ScrollEvent,
} from '@technology/web3d/dist/esnext/InputHandler.js';
import {
  animationFrameScheduler,
  debounceTime,
  filter,
  merge,
  mergeMap,
  Observable,
  pairwise,
  Subscription,
  tap,
} from 'rxjs';
import {
  Color,
  Fly,
  type IIntersection,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  type Model,
  Orbit,
  Pan,
  type ProjectionType,
  Quaternion,
  Ray,
  type SnapType,
  SphereGeometry,
  Vector3,
  Zoom,
} from '@technology/web3d';
import {
  MeshLine,
  MeshLineGeometry,
  Web3DLineMaterial,
} from '@technology/web3d-plugin-threejs';
import {
  type Entity,
  type IMetaMaterial,
  TrimbimModel,
} from '@technology/web3d-plugin-trimbim';
import {
  AlignmentPropertiesReader,
  isRail,
} from './alignment-properties-reader';
import {
  closestPointOnAlignment,
  countSegments,
  isLineGeometry,
  segmentAtChainage,
} from './alignment-geometry';
import { AlignmentPickingTooltip } from './alignment-picking-tooltip';
import { CameraRotationSmoother } from './camera-rotation-smoother';
import { CrossSectionController } from './cross-section-controller';
import {
  computeAlignmentDirection,
  computeSnapCameraPosition,
  isLookingBackwards,
} from './path-navigation-math';
import { AlignmentScrollNavigator } from './alignment-scroll-navigator';
import { PathNavigationViewControl } from './path-navigation-view-control';
import { PathNavigationToolEvent } from './path-navigation-tool-event';
import { CrossSectionContourOverlay } from './cross-section-contour-overlay';
import {
  addIntersection,
  createCaster,
  makeScreenStaticSize,
  MouseButtonValue,
  msElapsed,
  SnapTypeValue,
  Vector3Const,
  Web3DEventNameValue,
} from './web3d-internals';

interface AlignmentIntersection extends IIntersection {
  chainage: number;
}

export interface StartNavigationOptions {
  forceLookForward?: boolean;
  crossSectionEnabled?: boolean;
  section?: number;
  web3dChainage?: number;
  cameraPosition?: Vector3;
  cameraQuaternion?: Quaternion;
  orthoSize?: number;
  projectionType?: ProjectionType;
  direction?: 1 | -1;
}

const DEFAULT_MATERIAL: IMetaMaterial = {
  lineWidth: 2,
  color: new Color(0.0, 0.0, 0.0),
};
const DEFAULT_SECTION: { sectionName: string; material: IMetaMaterial } = {
  sectionName: 'unscanned',
  material: DEFAULT_MATERIAL,
};

export enum PathNavigationStatus {
  Inactive = 'inactive',
  Picking = 'picking',
  Navigating = 'navigating',
}

export type AlignmentMarkerMovedEvent = CustomEvent<{
  position: Vector3;
  quaternion: Quaternion;
}>;

export type AlignmentNameChangedEvent = CustomEvent<{
  name: string;
}>;

export type PathNavigationStatusChangedEvent = CustomEvent<{
  status: PathNavigationStatus;
  verticalPanOnly: boolean;
  crossSectionEnabled: boolean;
  pickingActive: boolean;
}>;

export type AlignmentPickingErrorEvent = CustomEvent<{
  message: string;
}>;

interface CameraMovement {
  axis: 'x' | 'y' | 'z' | 'rot';
  sign: number;
}

/**
 * The PathNavigationTool is designed to be used as a standalone tool.
 * Using other tools like Fly, Pan, Orbit, or Zoom is not necessary and not advisable
 * as the controls will overlap and may cause conflicts.
 *
 * @extends PathNavigationViewControl
 * @example
 * const viewer = new Web3DViewer();
 * viewer.tools.pathNavigation.enabled = true;
 */
export class PathNavigationTool extends PathNavigationViewControl {
  #pickingActive = false;
  #pickingActiveWithCtrlKey = false;
  readonly #api: Api;
  #subscribedToCursor3D = false;
  #subscribedItselfToCursor3D = false;
  #originalSubscriberSnapTypes: SnapType[] = [];

  readonly #cameraSmoother: CameraRotationSmoother;
  #cachedLookingBackwards = false; // Cached direction state
  #lookingBackwardsCacheValid = false; // Whether cache has been set
  #orbitActive = false;
  #orbitWasSmoothing = false;

  readonly #scrollNavigator = new AlignmentScrollNavigator({
    getChainage: (): number | undefined => this.chainage,
    setChainage: (chainage: number): Promise<void> =>
      this.setChainage(chainage),
    isCrossSectionEnabled: (): boolean => this.crossSectionEnabled,
    getCameraDistance: (): number => this.#cameraDistance,
    isLookingBackwards: (): boolean => this._lookingBackwards,
  });

  /** Wheel boost and glide; when disabled each wheel event moves by exactly its speed. */
  public get inertiaEnabled(): boolean {
    return this.#scrollNavigator.inertiaEnabled;
  }

  public set inertiaEnabled(enabled: boolean) {
    this.#scrollNavigator.inertiaEnabled = enabled;
  }

  /** Minimum scroll speed to trigger inertia (trackpad ~0.3, mouse wheel ~1.0+) */
  public get inertiaMinSpeed(): number {
    return this.#scrollNavigator.inertiaMinSpeed;
  }

  public set inertiaMinSpeed(speed: number) {
    this.#scrollNavigator.inertiaMinSpeed = speed;
  }

  get #cameraDistance(): number {
    return this.#cameraSmoother.targetDistance;
  }

  readonly #pointerMoveObservable: Observable<PointerInput>;
  #pointerMoveHandle: Subscription | undefined;

  readonly #tapObservable: Observable<PointerInput>;
  #tapHandle: Subscription | undefined;

  #disableExitNavigationOnModelRemove = false;
  readonly #alignmentProperties = new AlignmentPropertiesReader();
  readonly #pickingTooltip: AlignmentPickingTooltip;
  static readonly #alignmentSectionMaterialsCache: Map<
    string,
    { sectionName: string; material: IMetaMaterial }[]
  > = new Map();
  readonly #forwardOnAlignment: Vector3 = new Vector3();

  readonly #crossSection: CrossSectionController;
  readonly #crossSectionContourOverlay: CrossSectionContourOverlay | null =
    null;

  #updateContour(): void {
    if (!this.#crossSectionContourOverlay) return;
    const { near, far } = this.#crossSection.contourPlanes(this.#direction);
    this.#crossSectionContourOverlay.update(near, far, this.#api);
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention -- mirrors the static `Name` accessor of web3d tools
  public static get Name(): string {
    return 'pathNavigation';
  }
  #model!: TrimbimModel | undefined;
  #modelId!: string;
  #entityId!: number;
  #alignmentName: string = '';
  #alignmentModelId: string = '';
  #enabled = false;
  #direction: 1 | -1 = 1;

  public get direction(): 1 | -1 {
    return this.#direction;
  }

  public set direction(value: 1 | -1) {
    if (this.#direction === value) return;
    this.#direction = value;
    if (
      this._crossSectionEnabled &&
      this.status === PathNavigationStatus.Navigating
    ) {
      this.#setLookingBackwards(value === -1);
      this.#updateCrossSectionCamera();
      this.updateClipPlanes();
      this.#updateContour();
      this.#api.renderingManager.redraw();
    }
  }
  #navigationHandle: Subscription | undefined;

  // Keyboard
  readonly #keys: Map<string, CameraMovement>;
  #keyHandle: Subscription | undefined;
  readonly #keyObservable: Observable<KeyboardEvent>;
  readonly #mouseScrollObservable: Observable<ScrollEvent>;
  #moveHandle: Subscription | undefined;
  readonly #moveObservable: Observable<[number, number]>;
  #dir = new Vector3();
  public acceleration = 0.000005;
  #acceleration = this.acceleration;
  #maxSpeed = 0;
  readonly #minSpeed = 0.002;
  #speed = this.#minSpeed;

  readonly #tools: {
    zoom: Zoom;
    orbit: Orbit;
    fly: Fly;
    pan: Pan;
  };

  public override get name(): string {
    return PathNavigationTool.Name;
  }

  /**
   * Override to use cached direction during navigation.
   * This prevents direction flipping when camera is nearly perpendicular to alignment.
   * Cache is invalidated when user orbits the camera (changes orientation manually).
   */
  /** Caches the viewing side, which `direction` mirrors. */
  #setLookingBackwards(lookingBackwards: boolean): void {
    this.#cachedLookingBackwards = lookingBackwards;
    this.#lookingBackwardsCacheValid = true;
    this.#direction = lookingBackwards ? -1 : 1;
  }

  protected override get _lookingBackwards(): boolean {
    if (this.#lookingBackwardsCacheValid) {
      return this.#cachedLookingBackwards;
    }
    return isLookingBackwards(
      this.#api.camera.quaternion,
      this._currentAlignmentDirection
    );
  }

  #intersection: IIntersection | null = null;
  public intersectionChangedCallback:
    | ((intersection: IIntersection | null) => void)
    | null = null;

  public onMove: () => void = (): void => {
    if (this.#api.cursor.intersection?.point) {
      this.#intersection = this.#api.cursor.intersection;
      const model = this.#intersection.model;
      if (
        model instanceof TrimbimModel &&
        this.#api.models.get(model.modelId)
      ) {
        const hoveredEntityId = this.#intersection.id || 0;
        this.#pickingTooltip
          .show(async () => {
            const entityId =
              await this.#alignmentProperties.resolveNavigableEntityId(
                model,
                hoveredEntityId
              );
            return (
              (await this.#alignmentProperties.getDisplayName(
                model,
                entityId
              )) || model.modelId
            );
          })
          .catch((error: unknown) => {
            console.debug('Error resolving the alignment tooltip:', error);
          });
      }
      if (this.intersectionChangedCallback) {
        try {
          this.intersectionChangedCallback(this.#intersection);
        } catch (error) {
          console.debug('Error in intersectionChangedCallback:', error);
        }
      }
    }
  };

  constructor(api: Api) {
    super(api);
    this.#api = api;
    this.#pickingTooltip = new AlignmentPickingTooltip(api.cursor);
    this.#cameraSmoother = new CameraRotationSmoother(
      () => api.camera,
      () => this._centerMarker.position
    );
    this.#crossSection = new CrossSectionController(
      () => api.camera,
      () => api.renderingManager
    );
    this.#crossSectionContourOverlay = new CrossSectionContourOverlay(
      this.#api
    );
    this.#tools = {
      orbit: new Orbit(this.#api),
      fly: new Fly(this.#api.inputHandler, this.#api.camera),
      pan: new Pan(this.#api),
      zoom: new Zoom(this.#api),
    };
    this.#setPickingModeNavigationToolsEnabled(false);

    this.#api.eventDispatcher.subscribe(
      Web3DEventNameValue.modelUnload,
      (e: CustomEvent) => {
        this.removeModel(e.detail);
      }
    );

    // Keyboard
    this.#keys = new Map([
      ['ArrowUp', { axis: 'y', sign: 1 }],
      ['ArrowDown', { axis: 'y', sign: -1 }],
      ['ArrowLeft', { axis: 'x', sign: -1 }],
      ['ArrowRight', { axis: 'x', sign: 1 }],
    ]);
    this.#keyObservable = merge(
      api.inputHandler.keyDown$,
      api.inputHandler.keyUp$
    ).pipe(tap((e) => this._processKey(e)));

    this.#pointerMoveObservable = api.inputHandler.pointerMove$.pipe(
      tap((e) => {
        if (
          e.originalEvent instanceof MouseEvent &&
          (e.originalEvent.ctrlKey || e.originalEvent.metaKey)
        ) {
          this.subscribeToCursor3D();
          this.#pickingActiveWithCtrlKey = true;
          this.#pickingActive = true;
          this._dispatch(PathNavigationToolEvent.PathNavigationStatusChanged);
        } else if (
          e.originalEvent instanceof MouseEvent &&
          !(e.originalEvent.ctrlKey || e.originalEvent.metaKey) &&
          this.#pickingActiveWithCtrlKey
        ) {
          this.unsubscribeFromCursor3D();
          this.#pickingActiveWithCtrlKey = false;
          this.#pickingActive = false;
          this._dispatch(PathNavigationToolEvent.PathNavigationStatusChanged);
        }
      })
    );

    this.#tapObservable = this.#api.inputHandler
      .createTapObservable({ button: MouseButtonValue.left, touchCount: 1 })
      .pipe(
        filter(this.#ctrlKeyEventListenerCallback),
        mergeMap(addIntersection(this.#api.inputHandler.picker)),
        tap((e) => e.intersection && this.#handleIntersection(e.intersection))
      );

    this.#mouseScrollObservable = api.inputHandler.zoom$.pipe(
      tap((scrollEvent) => {
        // Zoom
        if (
          scrollEvent.originalEvent.ctrlKey ||
          scrollEvent.originalEvent.metaKey
        ) {
          this.#api.camera.lookAt(this._centerMarker.position);

          const currentDistance = this.#cameraDistance;
          const speed =
            Math.max(10, currentDistance) * -scrollEvent.speed * 0.05;
          // Prevent 1st person view
          if (Math.round(currentDistance) === 1 && scrollEvent.speed < 0) {
            if (this.#cameraSmoother.active) {
              this.#cameraSmoother.setDistance(1);
            } else {
              this.zoom = 1;
            }
          } else if (this._crossSectionEnabled) {
            this.#crossSection.zoom(scrollEvent.speed);
            this.#updateCrossSectionCamera();
          } else if (this.#api.camera.isPerspectiveCamera) {
            if (this.#cameraSmoother.active) {
              this.#cameraSmoother.setDistance(
                Math.max(1, this.#cameraSmoother.targetDistance - speed)
              );
            } else {
              this.#api.camera.position.add(
                this.#api.camera
                  .getWorldDirection(new Vector3())
                  .multiplyScalar(speed)
              );
            }
            this.#api.camera.callListeners();
          }
        } else {
          this.#scrollNavigator.scroll(scrollEvent.speed);
        }
        return scrollEvent;
      }),
      debounceTime(200)
    );

    this.#moveObservable = msElapsed(animationFrameScheduler).pipe(
      pairwise(),
      tap((t) => this._refreshMove(t[1] - t[0]))
    );

    // initialize alignment marker (up tack) and center marker (red sphere)
    this.#initializeAlignmentMarker();
    this.#initializeCenterMarker();

    makeScreenStaticSize(this._centerMarker, this.#api);
    this._centerMarker.renderOrder = 1;
  }

  readonly #ctrlKeyEventListenerCallback = (e: PointerInput): boolean => {
    return (
      e.originalEvent.ctrlKey || e.originalEvent.metaKey || this.#pickingActive
    );
  };

  public subscribeToCursor3D(): void {
    if (!this.#subscribedToCursor3D && this.enabled) {
      this.#subscribedToCursor3D = true;
      this.#setPickingModeNavigationToolsEnabled(false);
      this.#originalSubscriberSnapTypes = [...this.#api.cursor.snapTypes];
      if (!this.#api.cursor.isSubscribed()) {
        // calling subscribe is mandatory for the cursor to be calculated and displayed. The callback is optional
        // Could temporarily have our callback and set the previous callback when done with picking
        this.#api.cursor.snapTypes = [
          SnapTypeValue.LINE,
          SnapTypeValue.POINT,
          SnapTypeValue.FACE,
        ];
        this.#subscribedItselfToCursor3D = true;
      } else {
        if (!this.#api.cursor.snapTypes.includes(SnapTypeValue.LINE)) {
          this.#api.cursor.snapTypes.push(SnapTypeValue.LINE);
        }
        if (!this.#api.cursor.snapTypes.includes(SnapTypeValue.POINT)) {
          this.#api.cursor.snapTypes.push(SnapTypeValue.POINT);
        }
        if (!this.#api.cursor.snapTypes.includes(SnapTypeValue.FACE)) {
          this.#api.cursor.snapTypes.push(SnapTypeValue.FACE);
        }
        this.#subscribedItselfToCursor3D = false;
      }
      this.#api.eventDispatcher.dragStart(this); // disable conflicting tools (with left mouse click in our case)
      this.#api.cursor.subscribe(this.onMove); // // touching PickingTool.enabled resets the cursor, not a behavior we want.
    }
  }

  public unsubscribeFromCursor3D(): void {
    if (this.#subscribedToCursor3D) {
      this.#subscribedToCursor3D = false;
      this.#pickingTooltip.clear();
      if (this.#subscribedItselfToCursor3D) {
        this.#api.cursor.unsubscribe();
      }
      this.#api.eventDispatcher.dragEnd(this); // re-enable previously enabled tools
      if (this.status === PathNavigationStatus.Picking) {
        this.#setPickingModeNavigationToolsEnabled(true);
      }
      this.#api.cursor.snapTypes = [...this.#originalSubscriberSnapTypes];
      if (!this.#subscribedItselfToCursor3D) {
        this.#api.cursor.subscribe(); // touching PickingTool.enabled resets the cursor, not a behavior we want.
      }
    }
  }

  public get alignmentName(): string {
    return this.#alignmentName;
  }

  public get alignmentModelId(): string {
    return this.#alignmentModelId;
  }

  public get alignmentEntityId(): number {
    return this.#entityId;
  }

  public get status(): PathNavigationStatus {
    if (this.#alignmentName === '') {
      return this.enabled
        ? PathNavigationStatus.Picking
        : PathNavigationStatus.Inactive;
    } else {
      return PathNavigationStatus.Navigating;
    }
  }

  public get pickingActive(): boolean {
    return this.#pickingActive;
  }

  public set pickingActive(value: boolean) {
    if (this.#pickingActive === value) return;
    this.#pickingActive = value;
    if (this.#pickingActive) {
      this.subscribeToCursor3D();
    } else {
      this.unsubscribeFromCursor3D();
    }
    this._dispatch(PathNavigationToolEvent.PathNavigationStatusChanged);
  }

  /** The navigated point; undefined unless navigating. */
  public get currentAlignmentPoint(): Vector3 | undefined {
    return this.status === PathNavigationStatus.Navigating
      ? this._currentAlignmentPoint
      : undefined;
  }

  /** The alignment orientation at the navigated point; undefined unless navigating. */
  public get currentAlignmentDirection(): Quaternion | undefined {
    return this.status === PathNavigationStatus.Navigating
      ? this._currentAlignmentDirection
      : undefined;
  }

  public set autoPilot(value: boolean) {
    this.#scrollNavigator.autoPilot = value;
  }

  public get autoPilot(): boolean {
    return this.#scrollNavigator.autoPilot;
  }

  public set autoPilotSpeed(speed: number) {
    this.#scrollNavigator.autoPilotSpeed = speed;
  }

  public get autoPilotSpeed(): number {
    return this.#scrollNavigator.autoPilotSpeed;
  }

  /** Distance from camera to the center marker */
  public get zoom(): number {
    return this.#api.camera.position.distanceTo(this._centerMarker.position);
  }

  public set zoom(distance: number) {
    if (distance === 0) return;
    this.#api.camera.position.copy(
      this._centerMarker.position
        .clone()
        .add(
          this.#api.camera
            .getWorldDirection(new Vector3())
            .multiplyScalar(-distance)
        )
    );
    this.#api.camera.callListeners();
  }

  #setPickingModeNavigationToolsEnabled(enabled: boolean): void {
    this.#tools.fly.enabled = enabled;
    this.#tools.orbit.enabled = enabled;
    this.#tools.pan.enabled = enabled;
    this.#tools.zoom.enabled = enabled;
  }

  #enterNavigation(): void {
    // Picking first alignment, start alignment navigation
    this.#setPickingModeNavigationToolsEnabled(false);
    this._startNavigationInputs();

    this.#api.scene.add(this._alignmentMarker);
    this.#api.scene.add(this._centerMarker);
  }

  #exitNavigation(): void {
    this.#crossSectionTransitioning = false;
    {
      const wasCrossSection = this._crossSectionEnabled;
      if (this._crossSectionEnabled) {
        this._centerMarker.visible = true;
        this.#showCrossSectionOverlay(false);
        this.#crossSectionContourOverlay?.clear();
        this.#crossSection.restoreProjection();
        this.#crossSection.removeClipPlanes();
      }
      if (wasCrossSection && this.status === PathNavigationStatus.Navigating) {
        this._centerMarker.position.copy(this._currentAlignmentPoint);
        this.#api.camera.position.copy(
          computeSnapCameraPosition(
            this._currentAlignmentPoint,
            this.#forwardOnAlignment,
            this.#direction === -1
          )
        );
        this.#api.camera.lookAt(this._centerMarker.position);
        this.#api.camera.callListeners();
      }
      this._stopNavigationInputs();
      this.#setPickingModeNavigationToolsEnabled(true);

      this._alignmentMarker?.removeFromParent();
      this._centerMarker?.removeFromParent();
      this.#model = undefined;
      this.#modelId = '';
      this.autoPilot = false;
      this.#resetNavigationMotion();
      this.#api.renderingManager.redraw();
      this.#alignmentName = '';
      this.#alignmentModelId = '';
      this._dispatch(PathNavigationToolEvent.AlignmentNameChanged);
    }
  }

  /** Motion state is per navigation session; the frame loop driving it stops on exit and would resume stale. */
  #resetNavigationMotion(): void {
    this.#scrollNavigator.reset();
    this.#cameraSmoother.cancel();
    this.#lookingBackwardsCacheValid = false;
  }

  public exitNavigation(): void {
    if (this.enabled) {
      this.#exitNavigation();
      this._dispatch(PathNavigationToolEvent.PathNavigationStatusChanged);
    }
  }

  public _stopInputs(handles: Array<Subscription | undefined>): void {
    handles.forEach((handle) => handle?.unsubscribe());
  }

  public _stopNavigationInputs(): void {
    this._stopInputs([
      this.#navigationHandle,
      this._verticalPanDragHandle,
      this._panDragHandle,
      this._orbitDragHandle,
      this.#keyHandle,
      this.#moveHandle,
    ]);
    this.#navigationHandle = undefined;
    this._verticalPanDragHandle = undefined;
    this._panDragHandle = undefined;
    this._orbitDragHandle = undefined;
    this.#keyHandle = undefined;
    this.#moveHandle = undefined;
  }

  public _stopPickingInputs(): void {
    this._stopInputs([this.#pointerMoveHandle, this.#tapHandle]);
    this.#pointerMoveHandle = undefined;
    this.#tapHandle = undefined;
  }

  public _exitTool(): void {
    this._stopPickingInputs();
    this.pickingActive = false;
    this.#exitNavigation();
    this.#setPickingModeNavigationToolsEnabled(false);
    this.#alignmentProperties.clearEntityLookups();
  }

  public _startPickingInputs(): void {
    this.#pointerMoveHandle = this.#pointerMoveObservable.subscribe();
    this.#tapHandle = this.#tapObservable.subscribe();
  }

  public _startNavigationInputs(): void {
    this.#navigationHandle = this.#mouseScrollObservable.subscribe();
    this._verticalPanDragHandle = this._verticalPanDragObservable.subscribe();
    this._panDragHandle = this._panDragObservable.subscribe();
    this._orbitDragHandle = this._orbitDragObservable.subscribe();
    this.#keyHandle = this.#keyObservable.subscribe();
    this.#moveHandle = this.#moveObservable.subscribe();
  }

  public override set inputsEnabled(inputsEnabled: boolean) {
    if (this.inputsEnabled === inputsEnabled) return;
    if (inputsEnabled) {
      this._startPickingInputs();
      if (this.#alignmentName !== '') {
        this._startNavigationInputs();
      } else {
        this.#setPickingModeNavigationToolsEnabled(true);
      }
    } else {
      this._stopPickingInputs();
      if (this.#alignmentName !== '') {
        this._stopNavigationInputs();
      } else {
        this.#setPickingModeNavigationToolsEnabled(false);
      }
    }
  }

  public override get inputsEnabled(): boolean {
    return !!this.#pointerMoveHandle;
  }

  public override get enabled(): boolean {
    return this.#enabled;
  }

  public override set enabled(enabled: boolean) {
    if (this.#enabled === enabled) return;
    if (enabled) {
      this._startPickingInputs();
      this.#setPickingModeNavigationToolsEnabled(true);
    } else {
      this._exitTool();
    }
    this.#enabled = enabled;
    this._dispatch(PathNavigationToolEvent.PathNavigationStatusChanged);
  }

  #crossSectionTransitioning = false;

  #updateCrossSectionCamera(): void {
    this.#crossSection.placeCamera(
      this._centerMarker.position,
      this.#forwardOnAlignment,
      this.#direction
    );
  }

  async #enterCrossSectionView(
    direction: 1 | -1 = this._lookingBackwards ? -1 : 1
  ): Promise<void> {
    if (this.#crossSectionTransitioning) {
      console.warn('enterCrossSectionView: already transitioning, skipping');
      return;
    }
    this.#crossSectionTransitioning = true;
    this.#crossSection.enterProjection();

    await this.#ensureAlignmentIsUnselected();
    this.#setLookingBackwards(direction === -1);
    this.snapAlongAlignment();

    this.verticalPanOnly = false;
    this._centerMarker.visible = false;
    this.#showCrossSectionOverlay(true);

    this.updateClipPlanes();
    this.#api.camera.updateProjectionMatrix();

    this.#updateContour();
    this.#crossSectionTransitioning = false;
  }

  async #exitCrossSectionView(): Promise<void> {
    if (this.#crossSectionTransitioning) {
      console.warn('exitCrossSectionView: already transitioning, skipping');
      return;
    }
    this.#crossSectionTransitioning = true;
    this.snapToAlignment();

    this._centerMarker.visible = true;
    this.#showCrossSectionOverlay(false);

    this.#crossSectionContourOverlay?.clear();
    this.#crossSection.restoreProjection();
    this.#crossSection.removeClipPlanes();
    this.#crossSectionTransitioning = false;
  }

  public get centerMarker(): Mesh {
    return this._centerMarker;
  }

  public get alignmentMarker(): Mesh {
    return this._alignmentMarker;
  }

  public get crossSectionDepth(): number {
    return this.#crossSection.depth;
  }

  public set crossSectionDepth(depth: number) {
    this.#crossSection.depth = depth;
    if (
      this._crossSectionEnabled &&
      this.status === PathNavigationStatus.Navigating
    ) {
      this.updateClipPlanes();
      this.#updateCrossSectionCamera();
      this.#updateContour();
    }
  }

  public set crossSectionEnabled(enabled: boolean) {
    if (this._crossSectionEnabled === enabled) return;
    this._crossSectionEnabled = enabled;

    if (this.status === PathNavigationStatus.Navigating) {
      void (async (): Promise<void> => {
        if (enabled) {
          await this.#enterCrossSectionView();
        } else {
          await this.#exitCrossSectionView();
        }
      })().then(() => {
        this._dispatch(PathNavigationToolEvent.PathNavigationStatusChanged);
      });
    }
  }

  public get crossSectionEnabled(): boolean {
    return this._crossSectionEnabled;
  }

  public updateClipPlanes(): void {
    this.#crossSection.updateClipPlanes(
      this._currentAlignmentPoint,
      this._currentAlignmentDirection
    );
  }

  public temporarilyDisableExitNavigationOnModelRemove(): void {
    this.#disableExitNavigationOnModelRemove = true;
  }

  public removeModel(modelId: string): void {
    this.#alignmentProperties.invalidate(modelId);
    PathNavigationTool.#alignmentSectionMaterialsCache.delete(modelId);

    if (this.#modelId === modelId) {
      if (this.enabled) {
        if (!this.#disableExitNavigationOnModelRemove) {
          this.exitNavigation();
        } else {
          this.#disableExitNavigationOnModelRemove = false;
        }
      } else {
        this.#exitNavigation();
      }
    }
  }

  async #ensureAlignmentIsUnselected(): Promise<void> {
    const model = this.#model;
    if (this._crossSectionEnabled && model) {
      model.getEntities().then((entities) =>
        this.#api.selection.subtract(
          model.modelId,
          entities.map((entity: Entity) => entity.id)
        )
      );
    }
  }

  async #setCurrentAlignment(snapLine: AlignmentIntersection): Promise<void> {
    if (!snapLine.point || !snapLine.snapLineStart || !snapLine.snapLineEnd)
      return;
    this._currentAlignmentPoint = snapLine.point.clone();
    this._currentAlignmentDirection.setFromRotationMatrix(
      new Matrix4().lookAt(
        snapLine.snapLineStart,
        snapLine.snapLineEnd,
        Vector3Const.up
      )
    );
    this.#forwardOnAlignment.copy(
      Vector3Const.threejsCameraForward
        .clone()
        .applyQuaternion(this._currentAlignmentDirection)
    );
    this._currentLineSegmentId = Math.floor(snapLine.chainage);
    this._currentLineSegmentFraction =
      snapLine.point.distanceTo(snapLine.snapLineStart) /
      snapLine.snapLineStart.distanceTo(snapLine.snapLineEnd);
  }

  async #isAlignmentModel(model: Model): Promise<boolean> {
    if (!model || !(model instanceof TrimbimModel)) {
      return false;
    }
    return this.#alignmentProperties.isAlignmentModel(model);
  }

  readonly #handleIntersection = async (
    intersection: IIntersection
  ): Promise<void> => {
    if (
      intersection?.model &&
      intersection.model instanceof TrimbimModel &&
      intersection?.point
    ) {
      const model = intersection.model;
      const entityId = await this.#alignmentProperties.resolveNavigableEntityId(
        model,
        intersection.id
      );
      const supportedEntityIds =
        await this.#alignmentProperties.getSelectableEntityIds(model);
      if (supportedEntityIds.includes(entityId)) {
        const nextSnapLine = await model.pickFollowLine(
          entityId,
          new Ray(intersection.point.clone(), Vector3Const.up.clone())
        );
        if (!nextSnapLine) {
          console.debug(
            'PathNavigationTool: Cannot start navigation - failed to create intersection'
          );
          return;
        }
        const alignmentIntersection = {
          ...nextSnapLine,
          model: model,
          id: entityId,
          chainage: nextSnapLine.id,
        };
        this.#handleAlignmentIntersection(alignmentIntersection);
      } else {
        this._dispatch(PathNavigationToolEvent.AlignmentPickingError);
        console.debug(
          'Entity type not supported for alignment navigation:',
          this.#entityId
        );
      }
    } else if (intersection?.point) {
      // No alignment model in intersection, find the closest alignment across all TrimbimModels
      const trimbims = this.#api.models
        .getModels()
        .filter((model) => model instanceof TrimbimModel);
      const isAlignmentModel = await Promise.all(
        trimbims.map((model) =>
          this.#alignmentProperties.isAlignmentModel(model)
        )
      );
      const alignmentModels = trimbims.filter(
        (_, index) => isAlignmentModel[index]
      );

      if (alignmentModels.length === 0) {
        console.debug('PathNavigationTool: No alignment models found');
        return;
      }

      let closestResult:
        | {
            model: TrimbimModel;
            section: number;
            web3dChainage: number;
            distance: number;
            point: Vector3;
          }
        | undefined;

      for (const model of alignmentModels) {
        const result = await this.findClosestAlignmentPoint(
          model,
          intersection.point
        );
        if (
          result &&
          (!closestResult || result.distance < closestResult.distance)
        ) {
          closestResult = { ...result, model };
        }
      }

      if (!closestResult) {
        console.debug('PathNavigationTool: No closest alignment point found');
        return;
      }

      const closestIntersection = await this.#chainageToAlignmentIntersection(
        closestResult.model,
        closestResult.section,
        closestResult.web3dChainage
      );
      if (closestIntersection) {
        await this.#handleAlignmentIntersection(closestIntersection);
      }
    }
  };

  readonly #handleAlignmentIntersection = async (
    intersection: AlignmentIntersection,
    options: StartNavigationOptions = {}
  ): Promise<void> => {
    if (
      intersection?.model &&
      intersection.model instanceof TrimbimModel &&
      intersection?.point
    ) {
      this.#model = intersection.model;
      this.#modelId = this.#model.modelId;
      this.#entityId = intersection.id;
      if (this.status === PathNavigationStatus.Picking) {
        this.#enterNavigation();
      }

      const alignmentName =
        (await this.#alignmentProperties.getDisplayName(
          this.#model,
          this.#entityId
        )) || this.#model.modelId;
      this.#alignmentName = alignmentName;
      this.#alignmentModelId = this.#model.modelId;

      this.#setCurrentAlignment(intersection);

      // set markers to the picked point of the current line and look at the alignment direction.
      this._centerMarker.position.copy(this._currentAlignmentPoint);
      this._alignmentMarker.position.copy(this._currentAlignmentPoint);
      this._alignmentMarker.setRotationFromQuaternion(
        this._currentAlignmentDirection
      );

      if (options.cameraQuaternion && options.cameraPosition) {
        this.#api.camera.quaternion.copy(options.cameraQuaternion);
        this.#api.camera.position.copy(options.cameraPosition);
        this.#setLookingBackwards(
          isLookingBackwards(
            this.#api.camera.quaternion,
            this._currentAlignmentDirection
          )
        );
      } else {
        if (this._crossSectionEnabled) {
          await this.#enterCrossSectionView(
            options.forceLookForward
              ? 1
              : (options.direction ??
                  computeAlignmentDirection(
                    this.#api.camera.quaternion,
                    this._currentAlignmentDirection
                  ))
          );
        } else {
          const lookingBackwards =
            !options.forceLookForward && this._lookingBackwards;
          // set camera position before and above the snap line.
          this.#api.camera.position.copy(
            computeSnapCameraPosition(
              this._currentAlignmentPoint,
              this.#forwardOnAlignment,
              lookingBackwards
            )
          );

          this.#api.camera.lookAt(this._centerMarker.position);
          this.#setLookingBackwards(lookingBackwards);
        }
      }
      this.pickingActive = false;
      this._dispatch(PathNavigationToolEvent.PathNavigationStatusChanged);
      this._dispatch(PathNavigationToolEvent.AlignmentNameChanged);
      this._dispatch(PathNavigationToolEvent.AlignmentMarkerMoved);
      this.#api.camera.callListeners();
    }
  };

  public get chainage(): number | undefined {
    if (this.status === PathNavigationStatus.Navigating) {
      return this._currentLineSegmentId + this._currentLineSegmentFraction;
    }
    return undefined;
  }

  async #chainageToAlignmentIntersection(
    model: TrimbimModel,
    section: number,
    chainage: number
  ): Promise<AlignmentIntersection | undefined> {
    const entities = await this.getNavigableAlignmentEntities(model);
    const entityId = entities[section];
    const [mixedInstances] = await model.getGeometry([entityId]);
    const segment = segmentAtChainage(
      mixedInstances.filter(isLineGeometry),
      chainage,
      model.root.matrixWorld
    );
    if (!segment) return undefined;
    return {
      id: entityId,
      point: segment.point,
      snapLineStart: segment.start,
      snapLineEnd: segment.end,
      model: model,
      object: model.root,
      caster: createCaster(),
      chainage: chainage,
    };
  }

  public async setChainage(
    chainage: number,
    quaternion?: Quaternion
  ): Promise<void> {
    const model = this.#model;
    if (!model) return;
    // Navigation can be exited or switched to another model while geometry loads.
    const isStale = (): boolean => this.#model !== model;
    const [mixedInstances] = await model.getGeometry([this.#entityId]);
    if (isStale()) return;
    const instances = mixedInstances.filter(isLineGeometry);
    const maxChainage = countSegments(instances);

    const isAlignmentModel = await this.#isAlignmentModel(model);
    if (isStale()) return;
    if (isAlignmentModel && (chainage < 0 || chainage >= maxChainage)) {
      const entities = await this.getNavigableAlignmentEntities(model);
      if (isStale()) return;
      const entityIndex = entities.findIndex((e) => e === this.#entityId);
      if (chainage < 0) {
        if (entityIndex > 0) {
          this.#entityId = entities[entityIndex - 1];
          const [innerMixedInstances] = await model.getGeometry([
            this.#entityId,
          ]);
          if (isStale()) return;
          const innerMaxChainage = countSegments(
            innerMixedInstances.filter(isLineGeometry)
          );
          await this.setChainage(chainage + innerMaxChainage, quaternion);
        } else {
          await this.setChainage(0, quaternion);
        }
        return;
      } else if (chainage >= maxChainage) {
        if (entityIndex < entities.length - 1) {
          this.#entityId = entities[entityIndex + 1];
          await this.setChainage(chainage - maxChainage, quaternion);
        } else {
          await this.setChainage(maxChainage - 1e-4, quaternion);
        }
        return;
      }
      console.warn(
        `Chainage ${chainage} is out of bounds (0 - ${maxChainage}) for entity ${this.#entityId}`
      );
      return;
    } else if (chainage < 0) {
      chainage = 0;
    } else if (chainage > maxChainage) {
      chainage = maxChainage - 1e-4;
    }
    const segment = segmentAtChainage(
      instances,
      chainage,
      model.root.matrixWorld
    );
    if (!segment) return;
    try {
      await this.#navigateToKnownPosition(
        {
          point: segment.point,
          snapLineStart: segment.start,
          snapLineEnd: segment.end,
          id: segment.segmentIndex,
        } as IIntersection,
        quaternion
      );
    } catch (error) {
      console.error('Error navigating to known position:', error);
    }
  }

  // cache when only camera is rotating and target position is the same
  public previousTargetPosition: Vector3 = new Vector3();
  public previousSnapLine: IIntersection | null = null;

  public async navigateToPosition(
    targetPosition: Vector3,
    quaternion?: Quaternion
  ): Promise<void> {
    const model = this.#model;
    if (this.status === PathNavigationStatus.Navigating && model) {
      let nextSnapLine: IIntersection | undefined = targetPosition.equals(
        this.previousTargetPosition
      )
        ? (this.previousSnapLine ?? undefined)
        : undefined;

      if (!nextSnapLine?.point) {
        const navigableEntities =
          await this.getNavigableAlignmentEntities(model);
        let closestDistance = Infinity;
        let closestEntityId = this.#entityId;

        for (const entityId of navigableEntities) {
          const snapLine = await model.pickFollowLine(
            entityId,
            new Ray(targetPosition, Vector3Const.up.clone())
          );
          // Navigation can be exited or switched to another model while picking.
          if (this.#model !== model) return;
          if (snapLine?.point) {
            const distance = targetPosition.distanceTo(snapLine.point);
            if (distance < closestDistance) {
              closestDistance = distance;
              nextSnapLine = snapLine;
              closestEntityId = entityId;
            }
          }
        }

        if (nextSnapLine?.point) {
          this.#entityId = closestEntityId;
        }
      }
      if (nextSnapLine?.point) {
        this.#navigateToSnapLine(nextSnapLine, quaternion);
        this.previousTargetPosition.copy(targetPosition);
        this.previousSnapLine = nextSnapLine;
      }
    }
  }

  async #navigateToKnownPosition(
    targetIntersection: IIntersection,
    quaternion?: Quaternion
  ): Promise<void> {
    if (
      this.status === PathNavigationStatus.Navigating &&
      targetIntersection.point
    ) {
      this.#navigateToSnapLine(targetIntersection, quaternion);
      this.previousTargetPosition.copy(targetIntersection.point);
      this.previousSnapLine = targetIntersection;
    }
  }

  public async navigateToChainage(
    entityId: number,
    chainage: number,
    quaternion?: Quaternion
  ): Promise<void> {
    if (this.status === PathNavigationStatus.Navigating) {
      if (this.#entityId !== entityId) {
        this.#entityId = entityId;
      }
      await this.setChainage(chainage, quaternion);
    }
  }

  async #getMaxWeb3dChainage(
    model: TrimbimModel,
    entityId: number
  ): Promise<number> {
    try {
      const [mixedInstances] = await model.getGeometry([entityId]);
      if (!mixedInstances || mixedInstances.length === 0) {
        return 0;
      }
      return countSegments(mixedInstances.filter(isLineGeometry));
    } catch (error) {
      console.debug('Error getting max chainage for entity:', entityId, error);
      return 0;
    }
  }

  public async findClosestAlignmentPoint(
    model: TrimbimModel,
    targetPosition: Vector3
  ): Promise<
    | {
        section: number;
        web3dChainage: number;
        distance: number;
        point: Vector3;
      }
    | undefined
  > {
    const navigableEntities = await this.getNavigableAlignmentEntities(model);
    if (navigableEntities.length === 0) {
      return undefined;
    }

    let closestResult:
      | {
          section: number;
          web3dChainage: number;
          distance: number;
          point: Vector3;
        }
      | undefined;
    for (
      let sectionIndex = 0;
      sectionIndex < navigableEntities.length;
      sectionIndex++
    ) {
      const entityId = navigableEntities[sectionIndex];

      try {
        const [mixedInstances] = await model.getGeometry([entityId]);
        if (!mixedInstances || mixedInstances.length === 0) continue;

        const closest = closestPointOnAlignment(
          mixedInstances.filter(isLineGeometry),
          targetPosition
        );
        if (
          closest &&
          (!closestResult || closest.distance < closestResult.distance)
        ) {
          closestResult = { ...closest, section: sectionIndex };
        }
      } catch (error) {
        console.debug(
          'Error processing entity for closest point:',
          entityId,
          error
        );
      }
    }

    return closestResult;
  }

  public async getNavigationState(): Promise<
    StartNavigationOptions | undefined
  > {
    const model = this.#model;
    if (this.status !== PathNavigationStatus.Navigating || !model)
      return undefined;
    return {
      crossSectionEnabled: this._crossSectionEnabled,
      section: (
        (await this.getNavigableAlignmentEntities(model)) || []
      ).findIndex((e) => e === this.#entityId),
      web3dChainage: this.chainage,
      cameraPosition: this.#api.camera.position.clone(),
      cameraQuaternion: this.#api.camera.quaternion.clone(),
      orthoSize: this.#api.camera.orthoSize,
      projectionType: this.#api.camera.projectionType,
      direction: this.#direction,
    };
  }

  public async startNavigation(
    modelId: string,
    options: StartNavigationOptions = {}
  ): Promise<void> {
    const { section, web3dChainage, direction } = options;

    if (direction !== undefined) {
      this.#direction = direction;
    }

    if (!this.#enabled) {
      console.debug(
        'PathNavigationTool: Cannot start navigation - tool is disabled'
      );
      return;
    }

    let model = this.#api.models
      .getModels(TrimbimModel)
      .find((m) => m.modelId === modelId);
    if (!model) {
      model = await new Promise<TrimbimModel | undefined>((resolve) => {
        const timeout = setTimeout(() => {
          this.#api.eventDispatcher.unsubscribe(
            Web3DEventNameValue.modelLoad,
            onModelLoad
          );
          console.debug(
            'PathNavigationTool: Cannot start navigation - model not loaded within timeout:',
            modelId
          );
          resolve(undefined);
        }, 30000);
        const onModelLoad = (e: CustomEvent): void => {
          if (e.detail === modelId) {
            clearTimeout(timeout);
            this.#api.eventDispatcher.unsubscribe(
              Web3DEventNameValue.modelLoad,
              onModelLoad
            );
            resolve(
              this.#api.models
                .getModels(TrimbimModel)
                .find((m) => m.modelId === modelId)
            );
          }
        };
        this.#api.eventDispatcher.subscribe(
          Web3DEventNameValue.modelLoad,
          onModelLoad
        );
      });
      if (!model) return;
    }

    const navigableEntities = await this.getNavigableAlignmentEntities(model);
    if (navigableEntities.length === 0) {
      console.debug(
        'PathNavigationTool: Cannot start navigation - no navigable entities in model:',
        modelId
      );
      return;
    }

    const closestPointResult =
      section === undefined && web3dChainage === undefined
        ? await this.findClosestAlignmentPoint(
            model,
            this.#model && this.status === PathNavigationStatus.Navigating
              ? this._currentAlignmentPoint
              : this.#api.camera.position.clone()
          )
        : await this.#clampSectionAndChainage(
            section ?? 0,
            web3dChainage ?? 0,
            model
          );

    const closestSection = closestPointResult?.section ?? 0;
    const closestChainage = closestPointResult?.web3dChainage ?? 0;

    const alignmentIntersection = await this.#chainageToAlignmentIntersection(
      model,
      closestSection,
      closestChainage
    );
    if (!alignmentIntersection) {
      console.debug(
        'PathNavigationTool: Cannot start navigation - failed to create alignment intersection'
      );
      return;
    }
    await this.#handleAlignmentIntersection(alignmentIntersection, options);
  }

  async #clampSectionAndChainage(
    section: number,
    chainage: number,
    model: TrimbimModel
  ): Promise<{ section: number; web3dChainage: number }> {
    const navigableEntities = await this.getNavigableAlignmentEntities(model);
    if (navigableEntities.length === 0) {
      return { section: 0, web3dChainage: 0 };
    }
    const clampedSection = Math.max(
      0,
      Math.min(section, navigableEntities.length - 1)
    );
    const maxChainage = await this.#getMaxWeb3dChainage(
      model,
      navigableEntities[clampedSection]
    );
    const clampedChainage = Math.max(
      0,
      Math.min(chainage, maxChainage - 0.0001)
    );
    return { section: clampedSection, web3dChainage: clampedChainage };
  }

  #navigateToSnapLine(
    nextSnapLine: IIntersection,
    quaternion?: Quaternion
  ): void {
    // translate camera and markers to the next snap line point while preserving distance, orbit and panning
    const previousDirection = new Quaternion().copy(
      quaternion ? this.#api.camera.quaternion : this._currentAlignmentDirection
    );
    const previousPoint = this._currentAlignmentPoint.clone();

    // While smoothing, the target values are the reference, not the mid-interpolation ones.
    const referenceCamQuat = this.#cameraSmoother.targetQuaternion.clone();
    const referenceCamPos = this.#cameraSmoother.targetPosition.clone();
    const originalCamDistance = this.#cameraSmoother.targetDistance;

    const alignmentIntersection = {
      ...nextSnapLine,
      model: this.#model,
      id: this.#entityId,
      chainage: nextSnapLine.id,
    } as AlignmentIntersection;
    this.#setCurrentAlignment(alignmentIntersection);

    const rotationBetweenLines = new Quaternion().multiplyQuaternions(
      quaternion || this._currentAlignmentDirection, // add the rotation which aligns with the quaternion or the alignment segment
      previousDirection.invert() // cancel the rotation which aligns with the previous alignment segment
    );

    // Compute final camera position and quaternion (original formula - preserves angle relative to alignment)
    const cameraRelativePositionToAlignment = new Vector3()
      .subVectors(referenceCamPos, previousPoint)
      .applyQuaternion(rotationBetweenLines);
    const finalCameraPos = this._currentAlignmentPoint
      .clone()
      .add(cameraRelativePositionToAlignment);

    const finalCameraQuat = quaternion
      ? quaternion.clone()
      : new Quaternion()
          .copy(referenceCamQuat)
          .premultiply(rotationBetweenLines);

    // Move markers instantly to final positions
    this._alignmentMarker.position.copy(this._currentAlignmentPoint);
    this._alignmentMarker.setRotationFromQuaternion(
      this._currentAlignmentDirection
    );

    const centerRelativePositionToAlignment = new Vector3()
      .subVectors(this._centerMarker.position, previousPoint)
      .applyQuaternion(rotationBetweenLines);
    this._centerMarker.position
      .copy(this._currentAlignmentPoint)
      .add(centerRelativePositionToAlignment);

    this.#setLookingBackwards(
      isLookingBackwards(finalCameraQuat, this._currentAlignmentDirection)
    );

    if (quaternion || this._crossSectionEnabled) {
      // Immediate camera update when quaternion is provided or in cross-section view
      this.#cameraSmoother.cancel();
      this.#api.camera.quaternion.copy(finalCameraQuat);
      this.#api.camera.position.copy(finalCameraPos);
    } else {
      this.#cameraSmoother.start(
        finalCameraQuat,
        finalCameraPos,
        originalCamDistance
      );
    }

    this._dispatch(PathNavigationToolEvent.AlignmentMarkerMoved);
    this.#api.camera.callListeners();
  }

  protected override _orbitDownCallback(event: PointerInput): PointerInput {
    this.#orbitActive = true;
    this.#orbitWasSmoothing = this.#cameraSmoother.active;
    // web3d only reports the release on the next pointer event, so a wheel right after
    // the drag must read the live orientation rather than the side cached before it.
    this.#lookingBackwardsCacheValid = false;
    return super._orbitDownCallback(event);
  }

  protected override _orbitUpCallback(event: PointerInput): PointerInput {
    const result = super._orbitUpCallback(event);

    // The user orbited to the final orientation, so the pending target is obsolete.
    if (this.#orbitWasSmoothing) {
      this.#cameraSmoother.cancel();
    }

    const previousDirection = this.#direction;
    this.#setLookingBackwards(
      isLookingBackwards(
        this.#api.camera.quaternion,
        this._currentAlignmentDirection
      )
    );
    if (this.#direction !== previousDirection) {
      this._dispatch(PathNavigationToolEvent.AlignmentMarkerMoved);
    }

    this.#orbitActive = false;
    this.#orbitWasSmoothing = false;
    return result;
  }

  protected override _panUpCallback(event: PointerInput): PointerInput {
    this.#cameraSmoother.retarget();
    return super._panUpCallback(event);
  }

  /**
   * Snap back to alignment at the specified height. Look at the alignment direction.
   * @param {number} height - The height to snap to. Defaults to 0.
   */
  public snapToAlignment(height: number = 0): void {
    if (this.status === PathNavigationStatus.Navigating) {
      if (this._crossSectionEnabled) {
        this._centerMarker.position.copy(this._currentAlignmentPoint);
        this.#updateCrossSectionCamera();
        this.updateClipPlanes();
        this.#updateContour();
      } else {
        const alignmentToNextPos = new Vector3()
          .copy(Vector3Const.forward)
          .applyQuaternion(this._currentAlignmentDirection)
          .multiplyScalar(height);
        const posChangeFromNow = this._currentAlignmentPoint
          .clone()
          .add(alignmentToNextPos)
          .sub(this._centerMarker.position);

        this._centerMarker.position.add(posChangeFromNow);

        this.#api.camera.position.copy(
          computeSnapCameraPosition(
            this._centerMarker.position,
            this.#forwardOnAlignment,
            this._lookingBackwards
          )
        );
        this.#api.camera.lookAt(this._centerMarker.position);
        this.#api.camera.callListeners();
      }
    }
  }

  public snapAlongAlignment(): void {
    if (this.status === PathNavigationStatus.Navigating) {
      const alignmentToNextPos = new Vector3()
        .copy(Vector3Const.forward)
        .applyQuaternion(this._currentAlignmentDirection)
        .multiplyScalar(0);
      const posChangeFromNow = this._currentAlignmentPoint
        .clone()
        .add(alignmentToNextPos)
        .sub(this._centerMarker.position);

      this._centerMarker.position.add(posChangeFromNow);

      this.#crossSection.resetViewSize();
      this.#updateCrossSectionCamera();
    }
  }

  // Keyboard
  protected _processKey(event: KeyboardEvent): void {
    const setAcceleration = (shiftDown: boolean): void => {
      this.#maxSpeed = shiftDown
        ? this.acceleration * 4000 * 5
        : this.acceleration * 4000;
      this.#acceleration = shiftDown
        ? this.acceleration * 20
        : this.acceleration;
    };
    if (this.#keys.has(event.code)) {
      const desc = this.#keys.get(event.code)!;
      const axis = desc.axis as 'x' | 'y' | 'z';
      if (desc.axis === 'rot') return; // 'rot' is not applicable to Vector3
      if (event.type === 'keydown') {
        // if vertical pan only is enabled, ignore horizontal movement
        if (this.verticalPanOnly && axis === 'x') return;
        this.#dir[axis] = desc.sign;
      } else {
        if (this.#dir[axis] === desc.sign) this.#dir[axis] = 0;
      }
      setAcceleration(event.shiftKey);
      if (this.#dir.equals(Vector3Const.zero)) this.#speed = this.#minSpeed;
    } else if (event.code === 'ShiftLeft') {
      setAcceleration(event.type === 'keydown');
    }
  }

  protected _refreshMove(delta: number): void {
    // The orbit drag owns the camera rotation while active.
    if (!this.#orbitActive && this.#cameraSmoother.update(delta)) {
      this.#api.camera.callListeners();
    }

    this.#scrollNavigator.onFrame();

    if (this.#dir.lengthSq() === 0) return;
    this.#speed = Math.min(
      this.#speed + delta * this.#acceleration,
      this.#maxSpeed
    );

    const speed =
      this.#dir.x !== 0 && this._lookingBackwards ? -this.#speed : this.#speed;
    const add = this.#dir
      .clone()
      .applyQuaternion(this._currentAlignmentDirection)
      .multiplyScalar(delta * speed);
    // pan on a 2D plane perpendicular to the alignment direction.
    this.#api.camera.position.add(add);
    this._centerMarker.position.add(add);
    this.#api.camera.callListeners();
  }

  #initializeAlignmentMarker(): void {
    const geometry = new MeshLineGeometry();
    geometry.setPositions(
      new Float32Array([
        -0.5,
        0,
        0, // Top left
        0.5,
        0,
        0, // Top right
        0,
        0,
        0, // Top center
        0,
        0.5,
        0, // Bottom center
      ])
    );
    // declare the outline material before
    const materialOutline = new Web3DLineMaterial(
      { color: 0xffffff, linewidth: 6, depthTest: true, depthWrite: true },
      this.#api.renderingManager.uniforms
    );
    const material = new Web3DLineMaterial(
      { color: 0x000000, linewidth: 2, depthTest: false, depthWrite: true },
      this.#api.renderingManager.uniforms
    );
    this._alignmentMarker = new MeshLine(geometry, materialOutline);
    this._alignmentMarker.add(new MeshLine(geometry, material));
  }

  #initializeCenterMarker(): void {
    const geometry = new SphereGeometry(0.5, 10, 10);
    const material = new MeshBasicMaterial({
      color: 0xff0000,
      depthTest: true,
      depthWrite: true,
    });

    this._centerMarker = new Mesh(geometry, material);
  }

  #crossSectionOverlay: HTMLDivElement | null = null;

  #showCrossSectionOverlay(show: boolean): void {
    if (show && !this.#crossSectionOverlay) {
      const el = document.createElement('div');
      el.style.cssText =
        'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:14px;height:14px;border-radius:50%;background:red;pointer-events:none;z-index:1;';
      this.#api.container.appendChild(el);
      this.#crossSectionOverlay = el;
    } else if (!show && this.#crossSectionOverlay) {
      this.#crossSectionOverlay.remove();
      this.#crossSectionOverlay = null;
    }
  }

  public async getNavigableAlignmentEntities(
    model: TrimbimModel
  ): Promise<number[]> {
    if (!model) return [];
    return this.#alignmentProperties.getNavigableEntityIds(model);
  }

  public async setSectionMaterials(
    model: TrimbimModel,
    materials: { sectionName: string; material: IMetaMaterial }[]
  ): Promise<void> {
    const allMaterials = [DEFAULT_SECTION, ...materials];
    PathNavigationTool.#alignmentSectionMaterialsCache.set(
      model.modelId,
      allMaterials
    );
    this.setLoadedSections(model, []);
  }

  public async setLoadedSections(
    model: TrimbimModel,
    sectionNames: string[]
  ): Promise<void> {
    const allSectionMaterials =
      PathNavigationTool.#alignmentSectionMaterialsCache.get(model.modelId) || [
        DEFAULT_SECTION,
      ];
    allSectionMaterials.forEach(({ sectionName, material }) => {
      this.setSectionMaterial(
        model,
        sectionName,
        sectionNames.includes(sectionName) ? material : DEFAULT_MATERIAL
      );
    });
  }

  public async setSectionMaterial(
    model: TrimbimModel,
    sectionName: string,
    material: IMetaMaterial
  ): Promise<void> {
    const entities = await model.getEntities();
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];
      const properties = await this.#alignmentProperties.findProperties(
        model,
        entity.id
      );
      if (properties?.tag === sectionName) {
        const color =
          !isRail(properties) || !material.color
            ? material.color
            : new Color(
                material.color.r / 2,
                material.color.g / 2,
                material.color.b / 2
              );
        await model.setCustomMaterial([entity.id], { ...material, color });
      }
    }
  }

  protected _dispatch(event: PathNavigationToolEvent): void {
    switch (event) {
      case PathNavigationToolEvent.AlignmentMarkerMoved:
        if (this._crossSectionEnabled) {
          this.updateClipPlanes();
          this.#updateContour();
        }
        this.#api.eventDispatcher.dispatch(
          new CustomEvent(event, {
            detail: {
              position: this._alignmentMarker.position.clone(),
              quaternion: this._alignmentMarker.quaternion.clone(),
            },
          })
        );
        break;
      case PathNavigationToolEvent.AlignmentNameChanged:
        this.#api.eventDispatcher.dispatch(
          new CustomEvent(event, {
            detail: {
              name: this.#alignmentName,
            },
          })
        );
        break;
      case PathNavigationToolEvent.PathNavigationStatusChanged:
        this.#api.eventDispatcher.dispatch(
          new CustomEvent(event, {
            detail: {
              status: this.status,
              verticalPanOnly: this.verticalPanOnly,
              crossSectionEnabled: this._crossSectionEnabled,
              pickingActive: this.#pickingActive,
            },
          })
        );
        break;
      case PathNavigationToolEvent.AlignmentPickingError:
        this.#api.eventDispatcher.dispatch(
          new CustomEvent(event, {
            detail: {
              message: 'No navigable object detected.',
            },
          })
        );
        break;
      default:
        break;
    }
  }
}

export { computeAlignmentDirection };
